import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranscriptMetadataService } from '../transcript-metadata/transcript-metadata.service';
import { TranscriptStatus, TranscriptType } from '../../constants';
import { RequiredFields } from '../../models/transcriptExtractionData.model';
import { BatchSisResponse, SisErrorCodes, SisResponse } from '../../models/sis-response.model';
import { ToastService } from '../../services/toast-messages/toast-messages.service';
import { TranscriptToExport } from '../../models/etrive.model';
import { isArray, TranslateService } from '@ngx-translate/core';
import { ItpExtractionDataService } from '../itp-extraction/itp-extraction-data.service';
import {
  ItpExtractionValidationService,
  TranscriptValidationResult,
  ValidationErrorType
} from '../itp-extraction/itp-extraction-validation.service';
import {
  EtriveConnectService,
  TranscriptBatchItem
} from '../etrive-connect/etrive-connect.service';
import { ItpIntegrationSourceService } from '../itp-integration/sources/itp-integration-source.service';
import { ConnectorExportService } from '../connector-export/connector-export.service';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

/**
 * Service responsible for exporting transcript data to SIS (Student Information System).
 * Handles both single and batch exports, including validation and error handling.
 *
 * While `feature-itp-high-school-transcripts` is off, exports still go through the legacy
 * eTrieve Connect source-mapped API. TODO: once that flag is fully rolled out, delete the
 * legacy path (sendThroughEtriveConnect, sendBatchThroughEtriveConnect, resolveSource,
 * buildSourceNotMappedResponse, the EtriveConnectService/ItpIntegrationSourceService
 * injections, and the itpTypeFilterFlag branches in sendToSisSingle/sendToSisBatch) and
 * always use the connector export API.
 */
@Injectable({
  providedIn: 'root'
})
export class TranscriptExportService {
  private etriveConnectService = inject(EtriveConnectService);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private itpExtractionDataService = inject(ItpExtractionDataService);
  private itpExtractionValidationService = inject(ItpExtractionValidationService);
  private itpIntegrationSourceService = inject(ItpIntegrationSourceService);
  private connectorExportService = inject(ConnectorExportService);
  private featureFlagService = inject(FeatureFlagService);

  private requiredFieldsByType: Partial<Record<TranscriptType, Promise<RequiredFields>>> = {};
  // once we create an integration step for highschool, we need to have this pull from the transcript itself
  private readonly transcriptMetadataTypeId = 0;

  // TODO: remove once feature-itp-high-school-transcripts is fully rolled out and the legacy
  // eTrieve Connect path below is deleted.
  itpTypeFilterFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-high-school-transcripts', false)
  );

  /**
   * Validates and sends multiple transcripts to SIS in a single batch.
   * Invalid transcripts are marked as failed before the batch is sent.
   * @param transcripts - Array of transcript data to export
   * @returns Array of SIS responses for each transcript, including both valid and invalid results
   */
  async sendToSis(
    transcripts: TranscriptToExport[],
    showBatchToasts: boolean = true
  ): Promise<BatchSisResponse[]> {
    if (!transcripts || transcripts.length === 0) {
      this.toastService.error('Export Failed', 'No transcripts to export');
      return [];
    }

    const unknownError = {
      success: false,
      code: SisErrorCodes.UNKNOWN_ERROR,
      message: 'Request failed'
    };

    if (showBatchToasts) {
      this.toastService.info(
        'Exporting to SIS',
        `Processing ${transcripts.length} transcript(s)...`
      );
    }

    const transcriptTypes = [...new Set(transcripts.map(t => t.transcriptType))];
    const requiredFieldsByType = await Promise.all(
      transcriptTypes.map(async type => [type, await this.getRequiredFields(type)] as const)
    );

    const requiredFieldsMap = new Map(requiredFieldsByType);

    const validationResults = this.itpExtractionValidationService.validateTranscripts(
      transcripts,
      requiredFieldsMap
    );
    const invalidTranscripts = validationResults.filter(r => !r.isValid);
    const validTranscripts = transcripts.filter(
      t => validationResults.find(r => r.transcriptId === t.transcriptId)?.isValid
    );

    const invalidResponses: BatchSisResponse[] =
      await this.rejectInvalidTranscripts(invalidTranscripts);

    if (validTranscripts.length === 0) {
      this.toastService.error(
        'ITP.exportFailed',
        this.translateService.instant('ITP.bulkExportFailedMessage', {
          failed: invalidTranscripts.length,
          total: transcripts.length
        }),
        15000
      );

      return invalidResponses;
    }

    try {
      let responses: BatchSisResponse[] | BatchSisResponse;
      if (!this.itpTypeFilterFlag()) {
        responses = await this.sendBatchThroughEtriveConnect(transcripts, validTranscripts);
      } else {
        responses = await this.connectorExportService.exportTranscripts(validTranscripts);
      }

      // if not an array, there was a higher level failure as opposed to transcript-level failures - resulting in all failling to export
      if (isArray(responses)) {
        for (const sisResponse of responses as BatchSisResponse[]) {
          const transcriptId = sisResponse.transcriptId || '';

          if (sisResponse.success) {
            await this.handleSuccess(transcriptId, !showBatchToasts);
          } else {
            await this.handleError(transcriptId, sisResponse, !showBatchToasts);
          }
        }
        if (showBatchToasts) {
          this.showBatchSummaryToasts(
            responses as BatchSisResponse[],
            invalidTranscripts.length,
            transcripts.length
          );
        }
        return [...(responses as BatchSisResponse[]), ...invalidResponses];
      } else {
        if (showBatchToasts) {
          this.showBatchSummaryToasts([], transcripts.length, transcripts.length);
        }
        transcripts.forEach(async t => {
          await this.handleError(t.transcriptId, unknownError, !showBatchToasts);
        });
        this.showErrorToast(responses as BatchSisResponse);
        return [responses as BatchSisResponse, ...invalidResponses];
      }
    } catch (error) {
      console.error('Send to SIS failed:', error);
      this.toastService.error('ITP.sisErrors.unknownError', 'ITP.errorMessage', 15000);

      return transcripts.map(t => ({
        ...unknownError,
        transcriptId: t.transcriptId
      }));
    }
  }

  /**
   * Legacy batch export through the eTrieve Connect source-mapped API.
   * TODO: remove this method (and resolveSource/buildSourceNotMappedResponse, if nothing
   * else uses them) once feature-itp-high-school-transcripts is fully rolled out and the
   * connector export API is the only batch export path.
   * @param transcripts - All transcripts submitted for the batch, valid and invalid
   * @param validTranscripts - The transcripts that passed validation and are sent to eTrieve
   * @param invalidTranscripts - The transcripts rejected before the batch was sent
   * @param invalidResponses - The pre-built responses for the invalid transcripts
   */
  private async sendBatchThroughEtriveConnect(
    transcripts: TranscriptToExport[],
    validTranscripts: TranscriptToExport[]
  ): Promise<BatchSisResponse[]> {
    const selectedSource = await this.resolveSource();

    if (!selectedSource) {
      return transcripts.map(t => this.buildSourceNotMappedResponse(t.transcriptId));
    }

    const batchItems: TranscriptBatchItem[] = validTranscripts.map(t => ({
      data: t.data,
      transcriptId: t.transcriptId,
      transcriptUrl: t.transcriptUrl
    }));

    const responses = await this.etriveConnectService.callSourceBatch(selectedSource, batchItems);

    return responses;
  }
  /**
   * Loads required fields configuration if not already loaded.
   * @param transcriptTypeId - The transcript type to load requirements for
   */
  private getRequiredFields(transcriptTypeId: TranscriptType): Promise<RequiredFields> {
    return (this.requiredFieldsByType[transcriptTypeId] ??=
      this.itpExtractionDataService.loadRequiredFields(transcriptTypeId));
  }

  /**
   * Resolves the integration source code for transcript data export.
   * Fetches the source fresh on each call to ensure the latest mapping is used.
   * TODO: remove once feature-itp-high-school-transcripts is fully rolled out.
   * @returns The integration source code, or an empty string if not found
   */
  private async resolveSource(): Promise<string> {
    const integrationSources =
      await this.itpIntegrationSourceService.getITPIntegrationSourceByTranscriptId(
        this.transcriptMetadataTypeId
      );

    return Array.isArray(integrationSources)
      ? (integrationSources.find((e: any) => e?.step === 'Transcript Data')?.code ?? '')
      : '';
  }

  /**
   * Builds a standard SOURCE_NOT_MAPPED error response and shows a toast.
   * TODO: remove once feature-itp-high-school-transcripts is fully rolled out.
   * @param transcriptId - The transcript ID for the response
   * @returns A BatchSisResponse indicating source not mapped
   */
  private buildSourceNotMappedResponse(transcriptId: string): BatchSisResponse {
    this.toastService.error('ITP.sisErrors.sourceNotMapped', 'ITP.errorMessage', 15000);
    return {
      success: false,
      code: SisErrorCodes.SOURCE_NOT_MAPPED,
      message: 'No integration source configured',
      transcriptId
    };
  }

  /**
   * Handles post-export success: unlocks the transcript, updates status, and optionally shows a toast.
   * @param transcriptId - The ID of the successfully exported transcript
   * @param showToast - Whether to display a success toast notification
   */
  private async handleSuccess(transcriptId: string, showToast: boolean): Promise<void> {
    try {
      if (showToast) {
        this.toastService.success('ITP.success', 'ITP.successMessage', 5000);
      }
      await this.transcriptMetadataService.saveStatus(transcriptId, TranscriptStatus.Exported);
    } catch (error) {
      console.error(`Failed to update transcript ${transcriptId} after successful export:`, error);
    }
  }

  /**
   * Handles SIS error: updates transcript status, logs details, and optionally shows a detailed error toast.
   * @param transcriptId - The ID of the transcript that failed to export
   * @param response - The SIS error response containing the error code and message
   * @param showToast - Whether to display a detailed error toast notification
   * @param studentId - Determines error message (no ID vs incorrect ID)
   */
  private async handleError(
    transcriptId: string,
    response: SisResponse,
    showToast: boolean,
    studentId?: string | number | null
  ): Promise<void> {
    if (showToast) {
      this.showErrorToast(response, studentId);
    }
    const errorCode = response.code || SisErrorCodes.UNKNOWN_ERROR;
    const isMatchingError =
      errorCode === SisErrorCodes.STUDENT_NOT_FOUND ||
      errorCode === SisErrorCodes.INSTITUTION_NOT_FOUND ||
      errorCode === SisErrorCodes.MULTIPLE_STUDENTS_MATCHED ||
      errorCode === SisErrorCodes.MULTIPLE_INSTITUTIONS_MATCHED;
    const isPartialUpload = errorCode === SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE;
    const newStatus = isPartialUpload
      ? TranscriptStatus.PartiallyExported
      : isMatchingError
        ? TranscriptStatus.FailedMatching
        : TranscriptStatus.FailedToExport;

    try {
      await this.updateStatusSilently(transcriptId, newStatus);
    } catch (error) {
      console.error(`Failed to update transcript ${transcriptId} status after error:`, error);
    }

    console.error('SIS Error Details:', {
      transcriptId,
      errorCode: response.code || SisErrorCodes.UNKNOWN_ERROR,
      statusCode: response.statusCode,
      message: response.message || 'ITP.errorMessage'
    });
  }

  /**
   * Displays the appropriate error toast based on the SIS error code.
   * @param response - The SIS error response
   * @param studentId - When provided and STUDENT_NOT_FOUND, shows invalidStudentId message instead
   */
  private showErrorToast(response: SisResponse, studentId?: string | number | null): void {
    const errorMessage = response.message || 'ITP.errorMessage';
    const errorCode = response.code || SisErrorCodes.UNKNOWN_ERROR;

    const errorToastMap: Record<string, [string, string]> = {
      [SisErrorCodes.DATA_VALIDATION]: ['ITP.sisErrors.dataValidation', errorMessage],
      [SisErrorCodes.STUDENT_NOT_FOUND]: [
        'ITP.sisErrors.studentNotFound',
        studentId ? 'ITP.sisErrors.invalidStudentIdMessage' : 'ITP.sisErrors.studentNotFoundMessage'
      ],
      [SisErrorCodes.INSTITUTION_NOT_FOUND]: ['ITP.sisErrors.institutionNotFound', errorMessage],
      [SisErrorCodes.SIS_UNAVAILABLE]: [
        'ITP.sisErrors.sisUnavailable',
        'ITP.sisErrors.sisUnavailableMessage'
      ],
      [SisErrorCodes.AUTHENTICATION_ERROR]: [
        'ITP.sisErrors.authenticationError',
        'ITP.sisErrors.authenticationErrorMessage'
      ],
      [SisErrorCodes.CONFIGURATION_ERROR]: [
        'ITP.sisErrors.configurationError',
        'ITP.sisErrors.configurationErrorMessage'
      ],
      [SisErrorCodes.CONNECT_DOWN]: [
        'ITP.sisErrors.connectDown',
        'ITP.sisErrors.connectDownMessage'
      ],
      [SisErrorCodes.SOURCE_INACTIVE]: [
        'ITP.sisErrors.connectDown',
        'ITP.sisErrors.connectDownMessage'
      ],
      [SisErrorCodes.SOURCE_NOT_MAPPED]: [
        'ITP.sisErrors.sourceNotMapped',
        'ITP.sisErrors.sourceNotMappedMessage'
      ]
    };

    if (errorCode === SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE) {
      this.toastService.warn('ITP.sisErrors.partialCourseUpload', errorMessage, 15000);
      return;
    }

    const [title, message] = errorToastMap[errorCode] || [
      'ITP.sisErrors.unknownError',
      errorMessage
    ];
    this.toastService.error(title, message, 15000);
  }

  /**
   * Marks invalid transcripts as FailedToExport and returns their error responses.
   * @param invalidTranscripts - Array of validation results for invalid transcripts
   * @returns Array of BatchSisResponse for each invalid transcript
   */
  private async rejectInvalidTranscripts(
    invalidTranscripts: TranscriptValidationResult[]
  ): Promise<BatchSisResponse[]> {
    for (const invalid of invalidTranscripts) {
      await this.updateStatusSilently(invalid.transcriptId, TranscriptStatus.FailedToExport);
    }

    return invalidTranscripts.map(r => ({
      success: false,
      code: SisErrorCodes.DATA_VALIDATION,
      message: this.getValidationErrorMessage(r.errorType),
      transcriptId: r.transcriptId
    }));
  }

  /**
   * Displays summary toast notifications for batch export results.
   * @param responses - The SIS responses from the batch export
   * @param invalidCount - The number of transcripts that failed validation before export
   * @param totalCount - The total number of transcripts in the batch
   */
  private showBatchSummaryToasts(
    responses: BatchSisResponse[],
    invalidCount: number,
    totalCount: number
  ): void {
    const successful = responses.filter(r => r.success).length;
    const failedFromSis = responses.filter(
      r => !r.success && r.code !== SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE && r.transcriptId
    ).length;
    const totalFailed = invalidCount + failedFromSis;

    if (successful > 0) {
      const message = this.translateService.instant('ITP.successBulkMessage', {
        successful,
        total: totalCount
      });
      this.toastService.success('ITP.success', message, 15000);
    }

    if (totalFailed > 0) {
      this.toastService.error(
        'ITP.exportFailed',
        this.translateService.instant('ITP.bulkExportFailedMessage', {
          failed: totalFailed,
          total: totalCount
        }),
        15000
      );
    }
  }

  /**
   *
   * @param errorType
   */
  private getValidationErrorMessage(errorType?: ValidationErrorType): string {
    if (errorType === ValidationErrorType.specialCharactersInName) {
      return 'ITP.sisErrors.specialCharactersInName';
    }
    return 'ITP.sisErrors.missingRequiredFields';
  }

  /**
   * Updates a transcript's status, logging any errors without throwing.
   * @param transcriptId - The transcript ID to update
   * @param status - The new status to set
   */
  private async updateStatusSilently(transcriptId: string, status: number): Promise<void> {
    try {
      await this.transcriptMetadataService.saveStatus(transcriptId, status);
    } catch (error) {
      console.error(`Failed to update status for transcript ${transcriptId}:`, error);
    }
  }
}
